import FingerprintJS from "@fingerprintjs/fingerprintjs";

interface FingerprintInfo {
  visitorId: string;
  score: number;
  platform: string;
  os: string;
  device: string;
  userAgent: string;
}

class FingerprintService {
  private rawFingerprint: string = "";
  private fingerprintData: FingerprintInfo | null = null;
  private isInitialized: boolean = false;

  private generateFullFingerprint(
    rawId: string,
    score: number,
    device: string,
  ): string {
    const confidenceHex = Math.round(score * 100)
      .toString(16)
      .padStart(2, "0");
    const deviceBase64 = btoa(encodeURIComponent(device));
    return `${rawId}.${confidenceHex}.${deviceBase64}`;
  }

  async initialize(): Promise<string> {
    try {
      if (this.isInitialized && this.fingerprintData) {
        return this.fingerprintData.visitorId;
      }

      const fp = await FingerprintJS.load();
      const result = await fp.get();

      this.rawFingerprint = result.visitorId;
      const deviceInfo = this.getDeviceInfo();

      const fullFingerprint = this.generateFullFingerprint(
        result.visitorId,
        result.confidence.score,
        deviceInfo,
      );

      this.fingerprintData = {
        visitorId: fullFingerprint,
        score: result.confidence.score,
        platform: this.getPlatformInfo(),
        os: this.getOSInfo(),
        device: deviceInfo,
        userAgent: navigator.userAgent,
      };

      this.isInitialized = true;

      console.group("设备指纹详细信息");
      console.log("完整指纹:", fullFingerprint);
      console.log("可信度:", this.fingerprintData.score);
      console.log("平台:", this.fingerprintData.platform);
      console.log("操作系统:", this.fingerprintData.os);
      console.log("设备:", this.fingerprintData.device);
      console.log("User Agent:", this.fingerprintData.userAgent);
      console.groupEnd();

      return fullFingerprint;
    } catch (error) {
      console.error("设备指纹初始化失败:", error);
      throw new Error("设备指纹初始化失败");
    }
  }

  private getPlatformInfo(): string {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes("arkweb")) return "HarmonyOS";
    if (ua.includes("windows")) return "Windows";
    if (ua.includes("macintosh") || ua.includes("mac os x")) return "macOS";
    if (ua.includes("linux")) return "Linux";
    if (ua.includes("android")) return "Android";
    if (ua.includes("iphone") || ua.includes("ipad") || ua.includes("ipod"))
      return "iOS";
    return "未知平台";
  }

  private getOSInfo(): string {
    const ua = navigator.userAgent;
    let osVersion = "";

    if (ua.includes("ArkWeb")) {
      const harmonyMatch = ua.match(/ArkWeb\/([\d.]+)/);
      return `HarmonyOS ${harmonyMatch ? harmonyMatch[1] : ""}`;
    }

    const windowsMatch = ua.match(/Windows NT (\d+\.\d+)/);
    if (windowsMatch) {
      const versionMap: { [key: string]: string } = {
        "10.0": "Windows 11/10",
        "6.3": "Windows 8.1",
        "6.2": "Windows 8",
        "6.1": "Windows 7",
        "6.0": "Windows Vista",
        "5.2": "Windows XP x64",
        "5.1": "Windows XP",
      };
      return versionMap[windowsMatch[1]] || `Windows NT ${windowsMatch[1]}`;
    }

    const macMatch = ua.match(/Mac OS X (\d+[._]\d+[._]\d+)/);
    if (macMatch) {
      osVersion = macMatch[1].replace(/_/g, ".");
      return `macOS ${osVersion}`;
    }

    const iosMatch = ua.match(/OS (\d+_\d+(_\d+)?)/);
    if (
      iosMatch &&
      (ua.includes("iPhone") || ua.includes("iPad") || ua.includes("iPod"))
    ) {
      osVersion = iosMatch[1].replace(/_/g, ".");
      return `iOS ${osVersion}`;
    }

    const androidMatch = ua.match(/Android (\d+(\.\d+)*)/);
    if (androidMatch) {
      return `Android ${androidMatch[1]}`;
    }

    if (ua.includes("linux")) {
      return "Linux";
    }

    return "未知系统";
  }

  private getDeviceInfo(): string {
    const ua = navigator.userAgent;

    if (ua.includes("ArkWeb")) {
      const deviceMatch = ua.match(/;\s*([^;]+(?:Build|ArkWeb)[^;]*);/);
      if (deviceMatch) {
        let device = deviceMatch[1]
          .replace(/Build|ArkWeb|HarmonyOS|[;]/gi, "")
          .replace(/\s+/g, " ")
          .trim();

        if (device.toLowerCase().includes("huawei"))
          device = device.replace(/huawei/i, "HUAWEI");
        if (device.toLowerCase().includes("honor"))
          device = device.replace(/honor/i, "HONOR");

        return device || "HarmonyOS Device";
      }
      return "HarmonyOS Device";
    }

    const iPhoneMatch = ua.match(/iPhone\s*(?:OS\s*[\d_]+)?/);
    if (iPhoneMatch) {
      return "Apple iPhone";
    }

    const iPadMatch = ua.match(/iPad\s*(?:OS\s*[\d_]+)?/);
    if (iPadMatch) {
      return "Apple iPad";
    }

    const androidDeviceMatch = ua.match(/;\s*([^;]+(?:Build|Android)[^;]*);/);
    if (androidDeviceMatch) {
      let device = androidDeviceMatch[1]
        .replace(/Build|Android|MIUI|[;]/gi, "")
        .replace(/\s+/g, " ")
        .trim();

      if (device.toLowerCase().includes("mi "))
        device = device.replace(/mi/i, "Xiaomi");
      if (device.toLowerCase().includes("redmi"))
        device = device.replace(/redmi/i, "Redmi");
      if (device.toLowerCase().includes("huawei"))
        device = device.replace(/huawei/i, "HUAWEI");
      if (device.toLowerCase().includes("honor"))
        device = device.replace(/honor/i, "HONOR");
      if (device.toLowerCase().includes("oppo"))
        device = device.replace(/oppo/i, "OPPO");
      if (device.toLowerCase().includes("vivo"))
        device = device.replace(/vivo/i, "vivo");

      return device;
    }

    if (ua.includes("Macintosh")) {
      return "Apple Mac";
    }
    if (ua.includes("Windows")) {
      return "Windows PC";
    }
    if (ua.includes("Linux")) {
      return "Linux PC";
    }

    return "未知设备";
  }

  async getFingerprint(): Promise<string> {
    if (!this.isInitialized || !this.fingerprintData) {
      return this.initialize();
    }
    return this.fingerprintData.visitorId;
  }

  async getFingerprintInfo(): Promise<FingerprintInfo | null> {
    if (!this.isInitialized || !this.fingerprintData) {
      await this.initialize();
    }
    return this.fingerprintData;
  }

  async resetFingerprint(): Promise<string> {
    this.rawFingerprint = "";
    this.fingerprintData = null;
    this.isInitialized = false;
    return this.initialize();
  }

  isFingerPrintInitialized(): boolean {
    return this.isInitialized && !!this.fingerprintData;
  }

  getCurrentFingerprintInfo(): FingerprintInfo | null {
    return this.fingerprintData;
  }

  private isHarmonyOSWeChat(): boolean {
    const ua = navigator.userAgent;
    return ua.includes("ArkWeb") && ua.includes("MicroMessenger");
  }
}

export const fingerprintService = new FingerprintService();
export type { FingerprintInfo };
